import React, { createContext, useContext, useEffect, useState } from "react";
import posthog from "posthog-js";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  deleteUser,
  User,
} from "firebase/auth";
import { auth } from "../firebase";

export type UserType = "restaurant" | "caviste" | null;

interface AuthContextValue {
  user: User | null;
  userType: UserType;
  loading: boolean;
  setUserType: (type: UserType) => void;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  logOut: () => Promise<void>;
  deleteAccount: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userType, setUserType] = useState<UserType>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        posthog.identify(firebaseUser.uid);
      } else {
        posthog.reset();
      }

      setUser(firebaseUser);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  async function signIn(email: string, password: string) {
    await signInWithEmailAndPassword(auth, email, password);
  }

  async function signUp(email: string, password: string) {
    await createUserWithEmailAndPassword(auth, email, password);
  }

  async function logOut() {
    await signOut(auth);
    posthog.reset();
    setUserType(null);
  }

  async function deleteAccount() {
    if (!user) return;
    await deleteUser(user);
    posthog.reset();
    setUserType(null);
  }

  return (
    <AuthContext.Provider value={{ user, userType, loading, setUserType, signIn, signUp, logOut, deleteAccount }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
